import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useCallback } from 'react'
import { useMutation as useConvexMutation, useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import { FlashcardCard } from '@/components/FlashcardCard'

export const Route = createFileRoute('/app/flashcards/create')({
  component: CreateFlashcardPage,
})

function CreateFlashcardPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to create flashcards</div>
      </div>
    )
  }

  return <CreateFlashcardContent userId={currentUser._id} />
}

function CreateFlashcardContent({ userId }: { userId: Id<'users'> }) {
  const navigate = useNavigate()
  const [deckId, setDeckId] = useState<Id<'flashcardDecks'> | ''>('')
  const [frontSpanish1, setFrontSpanish1] = useState('')
  const [frontSpanish2, setFrontSpanish2] = useState('')
  const [backEnglish1, setBackEnglish1] = useState('')
  const [backEnglish2, setBackEnglish2] = useState('')
  const [cardType, setCardType] = useState<'word' | 'phrase' | 'lyrics'>('lyrics')
  const [targetVerb, setTargetVerb] = useState('')
  const [priority, setPriority] = useState(5)
  const [tags, setTags] = useState('')
  const [notes, setNotes] = useState('')
  const [isPreviewFlipped, setIsPreviewFlipped] = useState(false)

  const decks = useConvexQuery(api.flashcards.getDecks, { userId })
  const createFlashcard = useConvexMutation(api.flashcards.createFlashcard)

  const handleSubmit = useCallback(async () => {
    if (!deckId) {
      alert('Please select a deck')
      return
    }

    const frontSpanish = [frontSpanish1.trim()].filter(Boolean)
    if (frontSpanish2.trim()) {
      frontSpanish.push(frontSpanish2.trim())
    }

    const backEnglish = [backEnglish1.trim()].filter(Boolean)
    if (backEnglish2.trim()) {
      backEnglish.push(backEnglish2.trim())
    }

    if (frontSpanish.length === 0 || backEnglish.length === 0) {
      alert('Please enter at least one line for both Spanish and English')
      return
    }

    try {
      await createFlashcard({
        userId,
        deckId: deckId as Id<'flashcardDecks'>,
        frontSpanish,
        backEnglish,
        cardType,
        targetVerb: targetVerb.trim() || undefined,
        priority,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        notes: notes.trim() || undefined,
      })

      // Reset form
      setFrontSpanish1('')
      setFrontSpanish2('')
      setBackEnglish1('')
      setBackEnglish2('')
      setTargetVerb('')
      setPriority(5)
      setTags('')
      setNotes('')

      alert('Flashcard created successfully!')
    } catch (error) {
      console.error('Failed to create flashcard:', error)
      alert('Failed to create flashcard')
    }
  }, [
    deckId,
    frontSpanish1,
    frontSpanish2,
    backEnglish1,
    backEnglish2,
    cardType,
    targetVerb,
    priority,
    tags,
    notes,
    userId,
    createFlashcard,
  ])

  const previewFrontSpanish = [frontSpanish1 || 'Spanish text...', frontSpanish2].filter(Boolean)
  const previewBackEnglish = [backEnglish1 || 'English text...', backEnglish2].filter(Boolean)

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-8 terminal-border p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [CREATE FLASHCARD]
          </h1>
          <p className="text-trail-tan font-mono text-sm sm:text-base">
            Create a new flashcard manually
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="space-y-6">
            <div className="terminal-border p-4 sm:p-6 bg-black">
              <h2 className="text-xl font-bold text-terminal-green mb-4 font-mono">
                [FLASHCARD DETAILS]
              </h2>
              <div className="space-y-4">
                {/* Deck Selection */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► DECK
                  </label>
                  <select
                    value={deckId}
                    onChange={(e) => setDeckId(e.target.value as Id<'flashcardDecks'>)}
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  >
                    <option value="">Select a deck...</option>
                    {decks?.map((deck) => (
                      <option key={deck._id} value={deck._id}>
                        {deck.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Card Type */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► CARD TYPE
                  </label>
                  <select
                    value={cardType}
                    onChange={(e) => setCardType(e.target.value as 'word' | 'phrase' | 'lyrics')}
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  >
                    <option value="word">Word</option>
                    <option value="phrase">Phrase</option>
                    <option value="lyrics">Lyrics</option>
                  </select>
                </div>

                {/* Spanish Lines */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► SPANISH (Line 1) <span className="text-trail-tan font-normal">*</span>
                  </label>
                  <input
                    type="text"
                    value={frontSpanish1}
                    onChange={(e) => setFrontSpanish1(e.target.value)}
                    placeholder="Enter Spanish text..."
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► SPANISH (Line 2) <span className="text-trail-tan font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={frontSpanish2}
                    onChange={(e) => setFrontSpanish2(e.target.value)}
                    placeholder="Enter second Spanish line..."
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  />
                </div>

                {/* English Lines */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► ENGLISH (Line 1) <span className="text-trail-tan font-normal">*</span>
                  </label>
                  <input
                    type="text"
                    value={backEnglish1}
                    onChange={(e) => setBackEnglish1(e.target.value)}
                    placeholder="Enter English translation..."
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► ENGLISH (Line 2) <span className="text-trail-tan font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={backEnglish2}
                    onChange={(e) => setBackEnglish2(e.target.value)}
                    placeholder="Enter second English line..."
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  />
                </div>

                {/* Target Verb */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► TARGET VERB <span className="text-trail-tan font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={targetVerb}
                    onChange={(e) => setTargetVerb(e.target.value)}
                    placeholder="Verb to highlight..."
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► PRIORITY: {priority}/10
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► TAGS <span className="text-trail-tan font-normal">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="tag1, tag2, tag3..."
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► NOTES <span className="text-trail-tan font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes..."
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => navigate({ to: '/app/flashcards' })}
                className="flex-1 px-6 py-3 bg-black text-trail-tan font-bold font-mono border-4 border-trail-tan hover:bg-trail-tan hover:text-black transition-all"
              >
                CANCEL
              </button>
              <button
                onClick={handleSubmit}
                disabled={!deckId || !frontSpanish1.trim() || !backEnglish1.trim()}
                className="flex-1 px-6 py-3 bg-terminal-green text-black font-bold font-mono text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green disabled:bg-gray-700 disabled:border-gray-700 disabled:text-gray-500 transition-all retro-glow"
              >
                CREATE FLASHCARD
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="terminal-border p-4 sm:p-6 bg-black">
            <h2 className="text-xl font-bold text-terminal-green mb-4 font-mono">
              [PREVIEW]
            </h2>
            <div className="mb-4">
              <button
                onClick={() => setIsPreviewFlipped(!isPreviewFlipped)}
                className="px-4 py-2 bg-sky-blue text-black font-bold font-mono text-sm border-2 border-sky-blue hover:bg-black hover:text-sky-blue transition-all"
              >
                {isPreviewFlipped ? 'SHOW FRONT' : 'FLIP CARD'}
              </button>
            </div>
            <FlashcardCard
              frontSpanish={previewFrontSpanish}
              backEnglish={previewBackEnglish}
              targetVerb={targetVerb || undefined}
              isFlipped={isPreviewFlipped}
              onFlip={() => setIsPreviewFlipped(!isPreviewFlipped)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

